<template>
    <div class="content-wrapper">
        <div class="row align-items-center">
            <div class="col-md-12 col-lg-6">
                <div class="d-flex align-items-center">
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb p-0 d-flex align-items-center mb-primary">
                            <li class="breadcrumb-item page-header d-flex align-items-center">
                                <h4 class="mb-0">{{ $t('lessons.lesson_details') }}</h4>
                            </li>
                            <template v-if="$can('view_lessons')">
                                <li class="ml-2">|</li>
                                <li>
                                    <a :href="backToCourse(this.courseId)" class="btn btn-link text-primary pl-2">
                                        {{ $t('lessons.back_to_all_courses') }}
                                    </a>
                                </li>
                            </template>
                        </ol>
                    </nav>
                </div>
            </div>
            <div class="col-md-12 col-lg-6">
                <div class="d-flex justify-content-lg-end mb-primary">
                    <button
                        type="button"
                        class="btn btn-success"
                        :disabled="loading"
                        @click="isNextLessonAvailable() ? submit() : goToNextLesson()"
                    >
                        <span v-if="loading" class="spinner-border spinner-border-sm mr-2" role="status"
                              aria-hidden="true"></span>
                        {{ isNextLessonAvailable() ? $t('save') : $t('next') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Lesson Details -->
        <div class="lesson-profile mb-primary">
            <div class="card card-with-shadow">
                <div class="card-body">
                    <app-overlay-loader v-if="loader"/>
                    <div class="d-flex flex-wrap">
                        <div v-if="lesson.video_url" class="embed-responsive embed-responsive-16by9 flex-shrink-0"
                             style="width: 60%;">
                            <iframe class="embed-responsive-item" :src="lesson.video_url" frameborder="0"
                                    allowfullscreen></iframe>
                        </div>
                        <br></br>
                        <div v-if="lesson.attachments && lesson.attachments.length" style="width: 60%;">
                            <h5 class="mb-3">Attachments</h5>
                            <div
                                v-for="(file, index) in lesson.attachments"
                                :key="file.id"
                                class="mb-4 relative"
                            >
                                <p><strong>Attachment {{ index + 1 }}:</strong> {{ file.path.split('/').pop() }}</p>

                                <div class="embed-responsive embed-responsive-16by9" style="position: relative;">
                                    <iframe
                                        :id="'attachment-' + index"
                                        :src="file.full_url"
                                        width="100%"
                                        height="500px"
                                        class="embed-responsive-item"
                                        allowfullscreen
                                    ></iframe>

                                    <button
                                        @click="openFullscreen(index)"
                                        style="background: none;position: absolute;border: none;top: 15px;font-size: 18px;right: 0px;z-index: 10;color: white;"
                                    >
                                        ⛶
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="details-section" style="width: 40%; padding-left: 8%; padding-right: 20px;">
                            <h4 class="mb-3 text-center">{{ lesson.title }}</h4>

                            <LessonMeta icon="user" labelKey="created_by" :value="lesson.creator?.full_name"/>
                            <LessonMeta icon="calendar" labelKey="created_at"
                                        :value="formatDateTimeToLocalDate(lesson.created_at)"/>
                            <LessonMeta icon="calendar" labelKey="updated_at"
                                        :value="formatDateTimeToLocalDate(lesson.updated_at)"/>
                        </div>
                    </div>
                    <div v-if="lesson.video_url" class="mt-3">
                        <a :href="lesson.video_url" target="_blank" rel="noopener noreferrer" class="btn btn-primary">
                            Open Video in New Tab
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <!-- Lesson Questions -->
        <div class="lesson-question mb-primary">
            <div class="card card-with-shadow">
                <div class="card-body">
                    <app-overlay-loader v-if="loader"/>
                    <form :data-url="updatedUrl" method="POST" ref="form" enctype="multipart/form-data">
                        <div v-for="(question, index) in lesson.questions" :key="question.id" class="question mb-4">
                            <p><strong>{{ index + 1 }}. {{ question.question_text }}</strong></p>

                            <div v-if="question.question_type === 'mcq'">
                                <app-form-group
                                    v-for="answer in question.answers"
                                    :key="answer.id"
                                    :label="''"
                                    type="radio"
                                    :radio-checkbox-name="'answers[' + question.id + ']'"
                                    :list="[{ id: answer.id, value: answer.answer_text }]"
                                    v-model="formData.answers[question.id]"
                                    required
                                />
                            </div>

                            <div v-else-if="question.question_type === 'text'">
                                <app-form-group
                                    type="textarea"
                                    :name="'answers[' + question.id + ']'"
                                    v-model="formData.answers[question.id]"
                                    required
                                />
                            </div>

                            <small v-if="$errorMessage(errors, 'answers.' + question.id)" class="text-danger">
                                {{ $errorMessage(errors, 'answers.' + question.id) }}
                            </small>
                        </div>

                        <div class="form-actions mt-2">
                            <button
                                type="button"
                                class="btn btn-success"
                                :disabled="loading"
                                @click="isNextLessonAvailable() ? submit() : goToNextLesson()"
                            >
                                <span v-if="loading" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                {{ isNextLessonAvailable() ? $t('save') : $t('next') }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import ModalMixin from "../../../../../../common/Mixin/Global/ModalMixin";
import {formatDateTimeToLocalDate} from "../../../../../../common/Helper/Support/DateTimeHelper";
import {axiosGet, axiosPost, urlGenerator} from "../../../../../../common/Helper/AxiosHelper";
import {mapState} from "vuex";
import {FormMixin} from "../../../../../../core/mixins/form/FormMixin";
import {COURSE_SUPPORT, COURSEWEB} from "../../../../../Config/ApiUrl";
import {collection} from "../../../../../../common/Helper/helpers";

export default {
    name: "CourseLessonDetails",
    mixins: [ModalMixin, FormMixin],
    props: {
        courseId: [String, Number],
        value: Boolean
    },
    data() {
        return {
            urlGenerator,
            formatDateTimeToLocalDate,
            message: '',
            errors: {},
            lesson: {},
            allLessons: [],
            loading: false,
            formData: {answers: {}},
            updatedUrl: '',
        };
    },
    mounted() {
        if (this.courseId) {
            this.fetchAllLessons(this.courseId);
        }
    },
    methods: {
        backToCourse(courseId) {
            return urlGenerator(`${COURSEWEB}`);
        },
        // getIframeSrc(file) {
        //     console.log('file',file.full_url.endsWith('.pdf'));
        //     if (file.full_url.endsWith('.pdf')) {
        //         // Use PDF.js viewer with disableDownload & disablePrint
        //         return '/pdfjs/web/viewer.html?file=' + encodeURIComponent(file.full_url) + '#disableDownload=true&disablePrint=true';
        //     }
        //     // Otherwise, load file directly (video, etc.)
        //     return file.full_url;
        // },
        openFullscreen(index) {
            const iframe = document.getElementById("attachment-" + index);

            if (iframe.requestFullscreen) {
                iframe.requestFullscreen();
            } else if (iframe.webkitRequestFullscreen) {
                iframe.webkitRequestFullscreen();
            } else if (iframe.msRequestFullscreen) {
                iframe.msRequestFullscreen();
            }

            // Add event listener for exit
            document.addEventListener("fullscreenchange", this.exitHandler);
        },

        exitHandler() {
            if (!document.fullscreenElement) {
                console.log("Exited fullscreen (Esc pressed or programmatically).");
                // cleanup if needed (like removing a class or resetting state)
                document.removeEventListener("fullscreenchange", this.exitHandler);
            }
        },
        fetchAllLessons(courseId) {
            if (!courseId) return;
            this.loading = true;

            axiosGet(`${this.apiUrl.COURSE_SUPPORT}/${courseId}/lessons`)
                .then(({data}) => {
                    if (Array.isArray(data)) {
                        this.allLessons = data;
                        this.lesson = data[0] || {}; // Optionally set first lesson
                    }
                })
                .catch(() => this.$toastr.e("Failed to load related lessons"))
                .finally(() => (this.loading = false));
        },

        submit() {
            const url = `${this.apiUrl.COURSE}/${this.lesson.course_id}/lessons/${this.lesson.id}/submit`;
            const formData = new FormData();
            // this.loading = true;
            Object.entries(this.formData.answers).forEach(([questionId, answerId]) => {
                if (answerId) {
                    formData.append(`answers[${questionId}]`, answerId);
                } else {
                    alert("not qualified");
                }
            });

            axiosPost(url, formData)
                .then(({data}) => {
                    this.handleSubmissionSuccess(data);
                    this.refreshPage(2000);
                })
                .catch((error) => this.handleSubmissionError(error));
        },

        goToNextLesson() {
            const formData = new FormData();

            Object.entries(this.formData.answers).forEach(([questionId, answerId]) => {
                if (answerId) {
                    formData.append(`answers[${questionId}]`, answerId);
                }
            });

            const url = `${this.apiUrl.APP_SELECTABLE}/${this.lesson.id}/next-lesson/${this.lesson.course_id}/next`;
            axiosPost(url, formData)
                .then(({data}) => {
                    this.handleSubmissionSuccess(data);
                    this.formData.answers = {};
                    this.lesson = data; // update current lesson
                })
                .catch((error) => this.handleSubmissionError(error));
        },

        handleSubmissionSuccess(data) {
            this.loading = false;
            this.closeModal();

            if (data.message) {
                this.$toastr.s(data.message);
            }

            this.$emit('submitted');
        },

        handleSubmissionError(error) {
            this.loading = false;
            this.message = '';

            if (error.response) {
                const {status, data} = error.response;

                if (status === 422) {
                    this.errors = data.errors;
                } else {
                    this.$toastr.e(data.message || error.response.statusText || "Something went wrong");
                    this.refreshPage(2000);
                }

                if (this.fieldStatus) {
                    this.fieldStatus.isSubmit = true;
                }
            } else {
                this.$toastr.e("Network error occurred");
            }
        },

        handleFileDownload(url) {
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            setTimeout(() => document.body.removeChild(iframe), 1000);
        },

        refreshPage(delay = 0) {
            if (delay > 0) {
                setTimeout(() => {
                    window.location.href = urlGenerator(`${this.apiUrl.COURSEWEB}`);
                }, delay);
            }
        },

        closeModal() {
            $('#course-lesson-detail-modal').modal('hide');
            $('.modal-backdrop').remove();
            this.showModal = false;
        },

        isNextLessonAvailable() {
            const lastLesson = collection(this.allLessons).last();
            return lastLesson?.id === this.lesson.id;
        }
    },
    watch: {
        courseId: {
            handler(newVal) {
                if (newVal) this.fetchAllLessons(newVal);
            },
            immediate: true
        },
        lesson: {
            handler(lesson) {
                if (lesson?.title) {
                    const titleParts = document.title.split(" - ");
                    titleParts[0] = lesson.title;
                    document.title = titleParts.join(" - ");
                }
            },
            deep: true
        }
    },
    components: {
        LessonMeta: {
            props: ['icon', 'labelKey', 'value'],
            template: `
                <div class="media mb-4">
                <div class="align-self-center mr-3">
                    <app-icon :name="icon"/>
                </div>
                <div class="media-body">
                    <p class="text-muted mb-0">{{ $t(labelKey) }}</p>
                    <p class="mb-0">{{ value || $t('not_added_yet') }}</p>
                </div>
                </div>
            `
        }
    },
    computed: {
        ...mapState({
            loader: state => state.loading
        })
    }
};
</script>
